
import {
  TARIFF_RECOMMENDATION_CLEAR,
  TARIFF_RECOMMENDATION_FAILURE,
  TARIFF_RECOMMENDATION_REQUEST,
  TARIFF_RECOMMENDATION_START,
  TARIFF_RECOMMENDATION_SUCCESS,
} from 'actions/designer/tariffRecommendationActions'
import omit from 'lodash/omit'
import { Reducer } from 'redux'
import { createSelector } from 'reselect'
import { RootState } from 'types/state'

export type TariffRecommendationStatus = 'pending' | 'fetching' | 'success' | 'error'

export interface TariffRecommendationState {
  system_uuid: string
  status: TariffRecommendationStatus
  response?: any
  error?: string
}

export interface TariffRecommendationReducerState {
  pendingQueue: string[] 
  processQueue: { [key: string]: TariffRecommendationState }
}

const defaultState: TariffRecommendationReducerState = {
  pendingQueue: [],
  processQueue: {},
}

export const tariffRecommendationReducer: Reducer<TariffRecommendationReducerState> = (
  previousState = defaultState,
  action
) => {
  const { type, payload } = action

  if (type === TARIFF_RECOMMENDATION_REQUEST) {
    const { system_uuid } = payload
    // Add to pending queue if not already there
    if (!previousState.pendingQueue.includes(system_uuid)) {
      return {
        ...previousState,
        pendingQueue: [...previousState.pendingQueue, system_uuid],
      }
    }
    return previousState
  } else if (type === TARIFF_RECOMMENDATION_START) {
    const { system_uuid } = payload
    const newPendingQueue = previousState.pendingQueue.filter((uuid) => uuid !== system_uuid)
    const existingState = previousState.processQueue[system_uuid]

    return {
      ...previousState,
      pendingQueue: newPendingQueue,
      processQueue: {
        ...previousState.processQueue,
        [system_uuid]: {
          system_uuid,
          status: 'fetching',
          // Preserve the old response so it can be shown as skeleton while fetching
          response: existingState?.response,
        },
      },
    }
  } else if (type === TARIFF_RECOMMENDATION_SUCCESS) {
    const { system_uuid, response } = payload

    if (!previousState.processQueue[system_uuid]) {
      return previousState
    }

    return {
      ...previousState,
      processQueue: {
        ...previousState.processQueue,
        [system_uuid]: {
          ...previousState.processQueue[system_uuid],
          status: 'success',
          response,
        },
      },
    }
  } else if (type === TARIFF_RECOMMENDATION_FAILURE) {
    const { system_uuid, error } = payload

    if (!previousState.processQueue[system_uuid]) {
      return previousState
    }

    return {
      ...previousState,
      processQueue: {
        ...previousState.processQueue,
        [system_uuid]: {
          ...previousState.processQueue[system_uuid],
          status: 'error',
          error,
        },
      },
    }
  } else if (type === TARIFF_RECOMMENDATION_CLEAR) {
    const { system_uuid } = payload

    if (system_uuid) {
      // Clear specific system
      const newPendingQueue = previousState.pendingQueue.filter((uuid) => uuid !== system_uuid)
      return {
        ...previousState,
        pendingQueue: newPendingQueue,
        processQueue: omit(previousState.processQueue, [system_uuid]),
      }
    } else {
      // Clear all
      return defaultState
    }
  }

  return previousState
}

const getPendingQueue = (state: RootState) => state.designer.tariffRecommendation.pendingQueue
const getProcessQueue = (state: RootState) => state.designer.tariffRecommendation.processQueue

const areAnyPendingOrFetchingMemoized = createSelector(
  [getPendingQueue, getProcessQueue],
  (pendingQueue, processQueue) =>
    pendingQueue.length > 0 ||
    Object.values(processQueue).some((item: TariffRecommendationState) => item.status === 'fetching')
)


const isSystemPendingMemoized = createSelector(
  [getPendingQueue, (_state: RootState, system_uuid: string | undefined) => system_uuid],
  (pendingQueue, system_uuid) => !!system_uuid && pendingQueue.includes(system_uuid)
)

const isSystemFetchingMemoized = createSelector(
  [getProcessQueue, (_state: RootState, system_uuid: string | undefined) => system_uuid],
  (processQueue, system_uuid) => {
    if (!system_uuid) return false
    return processQueue[system_uuid]?.status === 'fetching'
  }
)

const isSystemPendingOrFetchingMemoized = createSelector(
  [getPendingQueue, getProcessQueue, (_state: RootState, system_uuid: string | undefined) => system_uuid],
  (pendingQueue, processQueue, system_uuid) => {
    if (!system_uuid) return false
    return pendingQueue.includes(system_uuid) || processQueue[system_uuid]?.status === 'fetching'
  }
)

const getSystemStatusMemoized = createSelector(
  [getProcessQueue, (_state: RootState, system_uuid: string | undefined) => system_uuid],
  (processQueue, system_uuid): TariffRecommendationStatus | null => {
    if (!system_uuid) return null
    return processQueue[system_uuid]?.status || null
  }
)

const getSystemResponseMemoized = createSelector(
  [getProcessQueue, (_state: RootState, system_uuid: string | undefined) => system_uuid],
  (processQueue, system_uuid) => {
    if (!system_uuid) return null
    return processQueue[system_uuid]?.response || null
  }
)

const getSystemErrorMemoized = createSelector(
  [getProcessQueue, (_state: RootState, system_uuid: string | undefined) => system_uuid],
  (processQueue, system_uuid) => {
    if (!system_uuid) return null
    return processQueue[system_uuid]?.error || null
  }
)

export const tariffRecommendationSelectors = {
  isSystemPending: isSystemPendingMemoized,
  isSystemFetching: isSystemFetchingMemoized,
  isSystemPendingOrFetching: isSystemPendingOrFetchingMemoized,
  getSystemStatus: getSystemStatusMemoized,
  getSystemResponse: getSystemResponseMemoized,
  getSystemError: getSystemErrorMemoized,
  areAnyPendingOrFetching: areAnyPendingOrFetchingMemoized,
}
